import type { Job } from '@/types/job'
import { supabase } from '@/lib/supabase'

type JobUpdateCallback = (job: Job) => void
type ConnectionStatusCallback = (isConnected: boolean) => void

interface WebSocketConnection {
  ws: WebSocket
  jobId: string
  reconnectAttempts: number
  reconnectTimeout: NodeJS.Timeout | null
}

class WebSocketService {
  private connections: Map<string, WebSocketConnection> = new Map()
  private callbacks: Map<string, JobUpdateCallback[]> = new Map()
  private statusCallbacks: Set<ConnectionStatusCallback> = new Set()
  private connecting: Set<string> = new Set() // Track jobs currently being connected
  private maxReconnectAttempts = 3 // Reduced from 5 to avoid spam
  private reconnectDelay = 2000 // Increased from 1000ms
  private wsUnavailable = false // Track if WebSocket endpoint is unavailable

  async connect(jobId: string, onUpdate: JobUpdateCallback): Promise<() => void> {
    // Skip WebSocket if endpoint is known to be unavailable
    if (this.wsUnavailable) {
      console.debug(`WebSocket unavailable, skipping connection for job ${jobId}`)
      return () => {} // Return no-op cleanup
    }

    // Get token from Supabase session
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      console.error('No auth token found')
      return () => {}
    }

    // Add callback
    if (!this.callbacks.has(jobId)) {
      this.callbacks.set(jobId, [])
    }
    this.callbacks.get(jobId)!.push(onUpdate)

    // Connect if not already connected or currently connecting for this job
    if (!this.connections.has(jobId) && !this.connecting.has(jobId)) {
      this.connecting.add(jobId)
      try {
        await this.createConnection(jobId)
      } finally {
        this.connecting.delete(jobId)
      }
    }

    // Return cleanup function
    return () => {
      const callbacks = this.callbacks.get(jobId) || []
      const index = callbacks.indexOf(onUpdate)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
      if (callbacks.length === 0) {
        this.callbacks.delete(jobId)
        this.disconnect(jobId)
      }
    }
  }

  private async createConnection(jobId: string) {
    // Skip if WebSocket is known to be unavailable
    if (this.wsUnavailable) {
      console.debug(`WebSocket unavailable, skipping connection attempt for job ${jobId}`)
      return
    }

    // Double-check before creating WebSocket (race condition protection)
    if (this.connections.has(jobId)) {
      console.debug(`WebSocket connection already exists for job ${jobId}`)
      return
    }

    // Triple-check if currently connecting (additional race condition protection)
    if (this.connecting.has(jobId)) {
      console.debug(`WebSocket connection already in progress for job ${jobId}`)
      return
    }

    // Get token from Supabase session
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      console.error('No auth token found')
      return
    }

    const token = session.access_token

    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3000'
    const url = `${wsUrl}/api/v1/jobs/${jobId}/stream?token=${token}`

    // Final check before creating WebSocket
    if (this.wsUnavailable) {
      console.debug(`WebSocket unavailable, aborting connection creation for job ${jobId}`)
      return
    }

    const ws = new WebSocket(url)
    const connection: WebSocketConnection = {
      ws,
      jobId,
      reconnectAttempts: 0,
      reconnectTimeout: null,
    }

    ws.onopen = () => {
      console.log(`WebSocket connected for job ${jobId}`)
      connection.reconnectAttempts = 0
      this.notifyStatusChange(true)
    }

    ws.onmessage = (event) => {
      try {
        const job = JSON.parse(event.data) as Job
        const callbacks = this.callbacks.get(jobId) || []
        callbacks.forEach((callback) => callback(job))
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error)
      }
    }

    ws.onerror = (error) => {
      // Only log error if WebSocket is not yet marked as unavailable
      // This prevents spam when endpoint doesn't exist
      if (!this.wsUnavailable) {
        console.error(`WebSocket error for job ${jobId}:`, error)
      }
      // Don't attempt reconnect on error - let onclose handle it
    }

    ws.onclose = (event) => {
      const wasClean = event.wasClean
      const code = event.code
      const reason = event.reason || 'Unknown reason'
      
      // Only log if not already marked as unavailable (to reduce spam)
      if (!this.wsUnavailable || code !== 1006) {
        console.log(`WebSocket disconnected for job ${jobId}`, {
          code,
          reason,
          wasClean,
        })
      }
      
      this.connections.delete(jobId)
      this.notifyStatusChange(false)

      // Don't reconnect if:
      // - Connection was closed cleanly (1000) - likely intentional
      // - Server not found (1006) - WebSocket endpoint doesn't exist
      // - Unauthorized (1008) - Auth issue
      // - Max reconnect attempts reached
      const shouldNotReconnect = 
        wasClean ||
        code === 1006 || // Abnormal closure (server not found)
        code === 1008 || // Policy violation (auth)
        connection.reconnectAttempts >= this.maxReconnectAttempts

      if (shouldNotReconnect) {
        if (code === 1006) {
          // Mark WebSocket as unavailable to prevent future connection attempts
          if (!this.wsUnavailable) {
            this.wsUnavailable = true
            console.warn(`WebSocket endpoint not available. Falling back to polling for all jobs.`)
            // Disconnect all existing connections to prevent further attempts
            this.disconnectAll()
          }
        }
        return
      }

      // Attempt reconnect if there are still callbacks for this job
      const callbacks = this.callbacks.get(jobId)
      if (callbacks && callbacks.length > 0) {
        this.attemptReconnect(jobId, connection)
      }
    }

    this.connections.set(jobId, connection)
  }

  private attemptReconnect(jobId: string, oldConnection: WebSocketConnection) {
    // Don't attempt reconnect if WebSocket is marked as unavailable
    if (this.wsUnavailable) {
      return
    }

    if (oldConnection.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`Max reconnect attempts (${this.maxReconnectAttempts}) reached for job ${jobId}. WebSocket unavailable, using polling instead.`)
      return
    }

    oldConnection.reconnectAttempts++
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, oldConnection.reconnectAttempts - 1), // Exponential backoff
      30000 // Max 30 seconds
    )

    console.log(`Scheduling WebSocket reconnect for job ${jobId} in ${delay}ms (attempt ${oldConnection.reconnectAttempts}/${this.maxReconnectAttempts})`)

    oldConnection.reconnectTimeout = setTimeout(async () => {
      // Check again before attempting reconnect
      if (this.wsUnavailable) {
        return
      }
      const callbacks = this.callbacks.get(jobId)
      if (callbacks && callbacks.length > 0) {
        console.log(`Attempting to reconnect WebSocket for job ${jobId} (attempt ${oldConnection.reconnectAttempts})`)
        await this.createConnection(jobId)
      }
    }, delay)
  }

  disconnect(jobId: string) {
    const connection = this.connections.get(jobId)
    if (connection) {
      if (connection.reconnectTimeout) {
        clearTimeout(connection.reconnectTimeout)
      }
      connection.ws.close()
      this.connections.delete(jobId)
    }
  }

  disconnectAll() {
    this.connections.forEach((connection) => {
      if (connection.reconnectTimeout) {
        clearTimeout(connection.reconnectTimeout)
      }
      connection.ws.close()
    })
    this.connections.clear()
    this.callbacks.clear()
    this.connecting.clear() // Clear connecting set as well
    this.notifyStatusChange(false)
  }

  isConnected(jobId?: string): boolean {
    if (jobId) {
      const connection = this.connections.get(jobId)
      return connection !== undefined && connection.ws.readyState === WebSocket.OPEN
    }
    // Check if any connection is open
    return Array.from(this.connections.values()).some(
      (conn) => conn.ws.readyState === WebSocket.OPEN
    )
  }

  onStatusChange(callback: ConnectionStatusCallback): () => void {
    this.statusCallbacks.add(callback)
    return () => {
      this.statusCallbacks.delete(callback)
    }
  }

  private notifyStatusChange(isConnected: boolean) {
    this.statusCallbacks.forEach((callback) => callback(isConnected))
  }
}

// Singleton instance
let instance: WebSocketService | null = null

export function getWebSocketService(): WebSocketService {
  if (!instance) {
    instance = new WebSocketService()
  }
  return instance
}

// Export default instance for convenience (but prefer using getWebSocketService())
export default getWebSocketService()
